/* eslint-disable no-undef */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { tokenService } from '../services/tokenService';
import { onGaEvent } from '../utils/analytics';

import { isEventTrackingRoute } from './eventTrackingPolicy';

interface TrackedEvent {
  event_type: 'click' | 'form_submit' | 'ga_event';
  url: string;
  button_id: string;
  button_text: string;
  button_parent_class: string;
  form_data?: string;
  ga_event_name?: string;
  ga_params?: string;
  timestamp: string;
}

const WS_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/^http/, 'ws');
const FLUSH_INTERVAL_MS = 5000;
const FLUSH_THRESHOLD = 10;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

const isClickTarget = (el: HTMLElement): boolean => (
  el.tagName?.toLowerCase() === 'button'
  || (el.tagName?.toLowerCase() === 'input' && (el as HTMLInputElement).type === 'submit')
);

const findClickable = (t: EventTarget | null): HTMLElement | null => {
  let el = t as HTMLElement | null;

  while (el && el !== document.body) {
    if (isClickTarget(el)) return el;

    el = el.parentElement;
  }

  return null;
};

const btnText = (el: HTMLElement): string => (
  el.tagName.toLowerCase() === 'input'
    ? (el as HTMLInputElement).value || ''
    : (el.innerText || '').trim().slice(0, 200)
);

const toClickEvt = (el: HTMLElement): TrackedEvent => ({
  event_type: 'click', url: window.location.href,
  button_id: el.id || '', button_text: btnText(el),
  button_parent_class: el.parentElement?.className || '',
  timestamp: new Date().toISOString(),
});

const formFields = (form: HTMLFormElement): string => {
  const data: Record<string, string> = {};

  new FormData(form).forEach((v, k) => {
    if (v instanceof File) return;

    const inp = form.elements.namedItem(k) as HTMLInputElement | null;

    if (inp?.type !== 'password') data[k] = String(v);
  });

  return JSON.stringify(data);
};

const toFormEvt = (form: HTMLFormElement): TrackedEvent => {
  const btn = form.querySelector<HTMLElement>('button[type="submit"], input[type="submit"]');

  return {
    event_type: 'form_submit', url: window.location.href,
    button_id: btn?.id || '', button_text: btn ? btnText(btn) : '',
    button_parent_class: btn?.parentElement?.className || '',
    form_data: formFields(form), timestamp: new Date().toISOString(),
  };
};

// -- Flush hook --

const useFlush = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  bufferRef: React.MutableRefObject<TrackedEvent[]>,
) => {
  const flush = useCallback(() => {
    const ws = wsRef.current;

    if (!bufferRef.current.length || !ws || ws.readyState !== WebSocket.OPEN) return;

    try { ws.send(JSON.stringify(bufferRef.current)); bufferRef.current = []; } catch { /* noop */ }
  }, [wsRef, bufferRef]);

  const push = useCallback((evt: TrackedEvent) => {
    bufferRef.current.push(evt);

    if (bufferRef.current.length >= FLUSH_THRESHOLD) flush();
  }, [bufferRef, flush]);

  return { flush, push };
};

// -- WS lifecycle hook --

const closeWs = (wsRef: React.MutableRefObject<WebSocket | null>) => {
  if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); wsRef.current = null; }
};

const wsCleanup = (
  state: { alive: boolean; timer: ReturnType<typeof setTimeout> | null },
  timer: ReturnType<typeof setInterval>,
  flush: () => void,
  wsRef: React.MutableRefObject<WebSocket | null>,
) => {
  state.alive = false;
  clearInterval(timer);

  if (state.timer) clearTimeout(state.timer);

  flush();
  closeWs(wsRef);
};

const AUTH_CLOSE_CODES = new Set([4001, 4003, 1008, 1006]);

const handleWsClose = (
  event: CloseEvent,
  state: { attempt: number; timer: ReturnType<typeof setTimeout> | null; alive: boolean },
  wsRef: React.MutableRefObject<WebSocket | null>,
  doConnect: () => void,
) => {
  wsRef.current = null;

  if (!state.alive) return;

  if (AUTH_CLOSE_CODES.has(event.code)) {
    tokenService.refreshToken()
      .then(() => { if (state.alive) doConnect(); })
      .catch(() => { state.alive = false; });

    return;
  }

  state.attempt += 1;
  state.timer = setTimeout(doConnect, Math.min(RECONNECT_BASE_MS * Math.pow(2, state.attempt), RECONNECT_MAX_MS));
};

const createWsConnector = (
  state: { attempt: number; timer: ReturnType<typeof setTimeout> | null; alive: boolean },
  wsRef: React.MutableRefObject<WebSocket | null>,
  flush: () => void,
) => {
  const doConnect = (): void => {
    if (!state.alive) return;

    const token = tokenService.getAccessToken();

    if (!token) return;

    const ws = new WebSocket(`${WS_URL}/ws/events?token=${token}`);

    ws.onopen = () => { state.attempt = 0; flush(); };
    ws.onclose = (e: CloseEvent) => handleWsClose(e, state, wsRef, doConnect);
    wsRef.current = ws;
  };

  return doConnect;
};

const useWsLifecycle = (
  wsRef: React.MutableRefObject<WebSocket | null>,
  flush: () => void,
  isActive: boolean,
) => {
  const stateRef = useRef({ attempt: 0, timer: null as ReturnType<typeof setTimeout> | null, alive: true });

  useEffect(() => {
    const state = stateRef.current;

    state.alive = true;

    if (!isActive) return undefined;

    const doConnect = createWsConnector(state, wsRef, flush);

    doConnect();

    const timer = setInterval(flush, FLUSH_INTERVAL_MS);

    return () => wsCleanup(state, timer, flush, wsRef);
  }, [isActive, wsRef, flush]);
};

// -- Click tracker hook --

const clickHandler = (push: (e: TrackedEvent) => void) => (e: MouseEvent) => {
  const el = findClickable(e.target);

  if (el) push(toClickEvt(el));
};

const useClickTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    const handler = clickHandler(push);

    document.addEventListener('click', handler, true);

    return () => document.removeEventListener('click', handler, true);
  }, [isActive, push]);
};

// -- Form tracker hook --

const useFormTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    const handler = (e: Event) => {
      const form = e.target as HTMLFormElement;

      if (form.tagName?.toLowerCase() === 'form') push(toFormEvt(form));
    };

    document.addEventListener('submit', handler, true);

    return () => document.removeEventListener('submit', handler, true);
  }, [isActive, push]);
};

const useGaTracker = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useEffect(() => {
    if (!isActive) return undefined;

    return onGaEvent((name, params) => {
      push({
        event_type: 'ga_event', url: window.location.href,
        button_id: '', button_text: '', button_parent_class: '',
        ga_event_name: name, ga_params: params ? JSON.stringify(params) : undefined,
        timestamp: new Date().toISOString(),
      });
    });
  }, [isActive, push]);
};

const useTrackers = (isActive: boolean, push: (e: TrackedEvent) => void) => {
  useClickTracker(isActive, push);
  useFormTracker(isActive, push);
  useGaTracker(isActive, push);
};

// -- Main hook --

const useRefs = () => ({
  wsRef: useRef<WebSocket | null>(null),
  bufferRef: useRef<TrackedEvent[]>([]),
});

export const useEventTracker = (): void => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const isActive = !!(isAuthenticated && user && isEventTrackingRoute(pathname));
  const { wsRef, bufferRef } = useRefs();
  const { flush, push } = useFlush(wsRef, bufferRef);

  useWsLifecycle(wsRef, flush, isActive);
  useTrackers(isActive, push);
};
